import { useQuery } from '@tanstack/react-query'
import { usePublicClient } from 'wagmi'
import { referralManagerAbi } from '@/abi/referralManagerAbi'
import { getContractAddress } from '@/config/contracts'
import { useStreamInvalidation } from '@/hooks/chain/_streamInvalidation'
import { useOperatorSession } from '@/hooks/useOperatorSession'
import { fetchReferrers } from '@/lib/api'

export type ReferrerStatus = 'active' | 'suspended' | 'terminated'

export interface ReferrerRow {
    address: `0x${string}`
    code: string
    status: ReferrerStatus
    effectiveShareBps: number
    accrued: bigint
    lifetimeEarned: bigint
    lifetimeClaimed: bigint
}

// Enum order in ReferralManager.ReferrerStatus (None is unreachable for registered rows).
const STATUS_BY_INDEX: (ReferrerStatus | 'none')[] = ['none', 'active', 'suspended', 'terminated']

// The service indexes ReferrerRegistered at confirmation depth, so a fresh
// registration shows up a little after its stream event: keep a slow poll too.
const ROSTER_REFETCH_MS = 30_000

// The ReferralManager has no on-chain enumeration. The referrer set comes from
// blockpot-service (GET /v1/referrers, indexed from ReferrerRegistered logs);
// each row's current status, share and balances stay live eth_call reads.
// Any ReferralManager event on the service stream refetches the roster.
export function useReferrers(chainId: number) {
    const { status: sessionStatus } = useOperatorSession()
    const client = usePublicClient({ chainId })
    const manager = getContractAddress(chainId, 'referralManager')
    const enabled = sessionStatus === 'ready' && Boolean(client && manager)
    const queryKey = ['referrers', chainId, manager] as const

    useStreamInvalidation(chainId, enabled, (m) => m.contract === 'referralManager', [queryKey])

    const query = useQuery({
        queryKey,
        enabled,
        retry: false,
        refetchInterval: ROSTER_REFETCH_MS,
        queryFn: async ({ signal }): Promise<ReferrerRow[]> => {
            if (!client || !manager) return []
            const { referrers } = await fetchReferrers(chainId, signal)
            const rows = await Promise.all(
                referrers.map(async ({ address, code }) => {
                    const [info, shareBps] = await Promise.all([
                        client.readContract({
                            address: manager,
                            abi: referralManagerAbi,
                            functionName: 'referrers',
                            args: [address],
                        }),
                        client.readContract({
                            address: manager,
                            abi: referralManagerAbi,
                            functionName: 'effectiveShareBps',
                            args: [address],
                        }),
                    ])
                    const [statusIndex, , , accrued, lifetimeEarned, lifetimeClaimed] = info
                    const status = STATUS_BY_INDEX[Number(statusIndex)]
                    if (status === 'none') return null // defensive; unreachable for registered rows
                    return {
                        address,
                        code,
                        status,
                        effectiveShareBps: Number(shareBps),
                        accrued,
                        lifetimeEarned,
                        lifetimeClaimed,
                    }
                }),
            )
            return rows.filter((r): r is ReferrerRow => r !== null)
        },
    })

    return {
        referrers: query.data ?? [],
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    }
}
